/* ============================================================================
   Aquarylic Button Motion
   Version: 1.0.2
   Theme: Broadcast 8.1.1 + Qikify Slide Cart
   Purpose:
   - Lightweight visual loading feedback for real commerce buttons.
   - Supports Broadcast buttons, Qikify buttons and AI-generated custom buttons.
   - No cart layout changes.
   - No Qikify DOM movement.
   - No MutationObserver.
   - No fetch/XMLHttpRequest patching.
   - Does not prevent clicks.
   ============================================================================ */

(function () {
  'use strict';

  if (window.__aqButtonMotionLoaded) return;
  window.__aqButtonMotionLoaded = true;

  var CONFIG = {
    loadingClass: 'aq-motion-loading',
    minLoadingMs: 420,
    defaultMaxLoadingMs: 2400,
    cartMaxLoadingMs: 7000,

    buttonSelector: [
      /* Explicit opt-in for custom templates */
      '[data-aq-motion]',
      '.aq-motion-target',

      /* PDP / product forms */
      'form[action*="/cart/add"] button[type="submit"]',
      'form[action*="/cart/add"] input[type="submit"]',
      'form[action*="/cart/add"] [name="add"]',
      '[data-add-to-cart]',
      '[data-cart-bar-add-to-cart]',
      '.product-form__submit',
      '.product__submit__add',

      /* Collection / quick add */
      '.quick-add__button',
      '.product-upsell__btn',

      /* Qikify real actions */
      '.qsc2-checkout-button',
      '.qsc2-sf-discount__action-btn',
      '.qsc2-sf-content__action-btn',

      /* Aquarylic custom discount recovery button */
      '.aq-discount-cart-message__button',

      /* AI-generated Shopify custom blocks */
      'button[class*="ai-add-to-cart-btn-"]',
      'button[class*="ai-bulk-add-button-"]',
      'button[class*="ai-bundle-btn-"]',
      'button[data-variant-id][data-quantity]',
      'button[data-button][class*="bulk-add"]'
    ].join(','),

    excludedSelector: [
      /* Cart open/close/navigation */
      '[data-cart-toggle]',
      '[data-drawer-toggle]',
      '[data-popup-open]',
      '[data-popdown-close]',
      '.qsc2-drawer-header__close-button',
      '.qsc2-sf-content__close-btn',

      /* Qikify tabs must stay untouched */
      '.qsc2-side-feature-btn',

      /* Cart line item controls: keep them native */
      '.qsc2-product-item__quantity-btn',
      '.qsc2-product-item__quantity-input',
      '.qsc2-product-item__remove-btn',
      '.qsc2-quantity-btn--minus',
      '.qsc2-quantity-btn--plus',

      /* Menus/navigation/search/sliders */
      '.menu-toggle',
      '[aria-controls*="menu"]',
      '.search-popdown__submit',
      '.search-popdown__close__button',
      '.search-reset',
      '[data-button-arrow]',
      '[data-button-prev]',
      '[data-button-next]',
      '[data-thumb-link]',
      'summary',
      'details summary',
      '[role="tab"]'
    ].join(','),

    clearEvents: [
      'cart:updated',
      'cart:refresh',
      'ajaxProduct:added',
      'product:added',
      'theme:product:add',
      'qikify:cart:updated',
      'qikify:cart:open',
      'aq:cart:updated',
      'aq:cart-updated',
      'aq:cart-sync',
      'theme:cart:update',
      'theme:cart:change'
    ]
  };

  var activeButtons = new WeakMap();

  function getClosestActionElement(target) {
    if (!target || !target.closest) return null;

    return target.closest('button, a, input[type="submit"], [role="button"]');
  }

  function isElementVisible(element) {
    if (!element || !element.getBoundingClientRect) return false;

    var rect = element.getBoundingClientRect();

    return Boolean(
      rect.width > 0 &&
      rect.height > 0 &&
      window.getComputedStyle(element).visibility !== 'hidden'
    );
  }

  function isExcluded(button) {
    if (!button || !button.matches) return true;

    if (button.matches(CONFIG.excludedSelector)) return true;

    if (button.closest && button.closest('.aq-mobile-menu-fallback')) return true;

    /*
      Inside Qikify drawer, animate only actual CTA/action buttons.
      Never animate side tabs, quantity controls or close buttons.
    */
    if (
      button.closest &&
      button.closest('.qsc2-drawer') &&
      !button.matches(
        [
          '.qsc2-checkout-button',
          '.qsc2-sf-discount__action-btn',
          '.qsc2-sf-content__action-btn',
          '.aq-discount-cart-message__button',
          '[data-aq-motion]'
        ].join(',')
      )
    ) {
      return true;
    }

    return false;
  }

  function isMotionButton(button) {
    if (!button || !button.matches) return false;
    if (!button.matches(CONFIG.buttonSelector)) return false;
    if (!isElementVisible(button)) return false;
    if (button.disabled) return false;
    if (button.getAttribute('aria-disabled') === 'true') return false;
    if (isExcluded(button)) return false;

    return true;
  }

  function isCartLikeButton(button) {
    if (!button || !button.matches) return false;

    return button.matches(
      [
        'form[action*="/cart/add"] button',
        'form[action*="/cart/add"] input[type="submit"]',
        'form[action*="/cart/add"] [name="add"]',
        '[data-add-to-cart]',
        '[data-cart-bar-add-to-cart]',
        '.product-form__submit',
        '.product__submit__add',
        '.quick-add__button',
        '.product-upsell__btn',
        'button[class*="ai-add-to-cart-btn-"]',
        'button[class*="ai-bulk-add-button-"]',
        'button[class*="ai-bundle-btn-"]',
        'button[data-variant-id][data-quantity]',
        'button[data-button][class*="bulk-add"]',
        '[data-aq-motion="cart"]'
      ].join(',')
    );
  }

  function clearOne(button, immediate) {
    if (!button || !button.classList) return;

    var record = activeButtons.get(button);

    if (record && record.timeoutId) {
      window.clearTimeout(record.timeoutId);
    }

    function finish() {
      button.classList.remove(CONFIG.loadingClass);
      button.removeAttribute('aria-busy');
      button.removeAttribute('data-aq-motion-active');
      activeButtons.delete(button);
    }

    if (immediate || !record) {
      finish();
      return;
    }

    var elapsed = Date.now() - record.startedAt;
    var remaining = Math.max(CONFIG.minLoadingMs - elapsed, 0);

    window.setTimeout(finish, remaining);
  }

  function setLoading(button, options) {
    if (!button || !button.classList) return;
    if (!isMotionButton(button)) return;

    var settings = options || {};
    var maxLoadingMs = settings.maxLoadingMs || (
      isCartLikeButton(button)
        ? CONFIG.cartMaxLoadingMs
        : CONFIG.defaultMaxLoadingMs
    );

    clearOne(button, true);

    button.classList.add(CONFIG.loadingClass);
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('data-aq-motion-active', 'true');

    var timeoutId = window.setTimeout(function () {
      clearOne(button);
    }, maxLoadingMs);

    activeButtons.set(button, {
      startedAt: Date.now(),
      timeoutId: timeoutId
    });
  }

  function clearAll() {
    document.querySelectorAll('[data-aq-motion-active="true"]').forEach(function (button) {
      clearOne(button);
    });
  }

  function clearCartLikeButtons() {
    document.querySelectorAll('[data-aq-motion-active="true"]').forEach(function (button) {
      if (isCartLikeButton(button)) {
        clearOne(button);
      }
    });
  }

  function handleClick(event) {
    var button = getClosestActionElement(event.target);
    if (!button) return;

    if (!isMotionButton(button)) return;

    setLoading(button);
  }

  function handleSubmit(event) {
    var form = event.target;

    if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) {
      return;
    }

    var button =
      form.querySelector('button[type="submit"]:focus') ||
      form.querySelector('button[type="submit"]') ||
      form.querySelector('input[type="submit"]') ||
      form.querySelector('[name="add"]') ||
      form.querySelector('[data-add-to-cart]') ||
      form.querySelector('.product-form__submit') ||
      form.querySelector('.product__submit__add');

    if (button) {
      setLoading(button, {
        maxLoadingMs: CONFIG.cartMaxLoadingMs
      });
    }
  }

  function init() {
    document.addEventListener('click', handleClick, true);
    document.addEventListener('submit', handleSubmit, true);

    CONFIG.clearEvents.forEach(function (eventName) {
      document.addEventListener(eventName, function () {
        window.setTimeout(clearCartLikeButtons, 250);
      });
    });

    window.addEventListener('pageshow', clearAll);
    window.addEventListener('beforeunload', clearAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();